import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BasicChip } from '@/components/Chips/BasicChip';
import { Modal } from '@/components/Modal/Modal';
import { DcrContext } from '@/features/DataChangeRequest/_context/DcrContext';

import { useDcrReviewForm } from '../_hooks/useDcrReviewForm';
import { DcrReviewFooter } from '../DcrReviewFooter/DcrReviewFooter';
import { dcrReviewStyles } from '../DcrReviewFooter/DcrReviewFooter.styles';
import { FieldChange } from '../FieldChange';
import { ReviewGroupHeader } from '../ReviewGroupHeader';
import { WholeEntityChange } from '../WholeEntityChange';

import { confirmSubmitModalStyles as sx } from './ConfirmSubmitModal.styles';

export type ConfirmChangeEntry = {
	label: string;
	previousDisplay: string | null;
	newDisplay: string;
	isNewField: boolean;
};

export type ConfirmGroupEntry = {
	kind: 'group';
	entityLabel: string;
	summary?: string;
	fields?: ConfirmChangeEntry[];
	rows?: Array<{
		previousDisplay: string | null;
		newDisplay: string | null;
		isPrimary?: boolean;
	}>;
	wholeItems?: Array<{
		action: 'added' | 'removed';
		value: string;
		chip?: string | string[];
	}>;
};

export type ConfirmEntry = ConfirmChangeEntry | ConfirmGroupEntry;

const isGroupEntry = (entry: ConfirmEntry): entry is ConfirmGroupEntry =>
	'kind' in entry && entry.kind === 'group';

type Props = {
	open: boolean;
	changes: ConfirmEntry[];
	submitting: boolean;
	onCancel: () => void;
	onConfirm: (
		notes: string | undefined,
		thirdPartyConfirmed: boolean,
		attachmentIds: string[]
	) => void;
};

export const ConfirmSubmitModal = ({
	open,
	changes,
	submitting,
	onCancel,
	onConfirm
}: Props) => {
	const { t } = useTranslation();

	const {
		methods,
		isThirdPartyConfirmed,
		attachments,
		addFiles,
		removeAttachment,
		attachmentsExpanded,
		setAttachmentsExpanded,
		dcrContextValue,
		buildSubmitArgs,
		markCancelled,
		handleExited
	} = useDcrReviewForm(open);

	const [displayChanges, setDisplayChanges] = useState(changes);
	if (open && displayChanges !== changes) {
		setDisplayChanges(changes);
	}

	const handleConfirm = () => {
		onConfirm(...buildSubmitArgs());
	};

	const handleCancel = () => {
		markCancelled();
		onCancel();
	};

	return (
		<FormProvider {...methods}>
			<DcrContext.Provider value={dcrContextValue}>
				<Modal
					open={open}
					onClose={handleCancel}
					onExited={handleExited}
					size='sm'
					width={480}
					disableShadow
					disableFooterShadow
					title={t('Review your change')}
					footer={
						<DcrReviewFooter
							control={methods.control}
							attachments={attachments}
							addFiles={addFiles}
							removeAttachment={removeAttachment}
							attachmentsExpanded={attachmentsExpanded}
							onExpandAttachments={() => setAttachmentsExpanded(true)}
							actions={
								<Box sx={sx.actionsRow}>
									<Button
										variant='text'
										onClick={handleCancel}
										disabled={submitting}
									>
										{t('Cancel')}
									</Button>
									<Button
										variant='contained'
										onClick={handleConfirm}
										disabled={submitting || !isThirdPartyConfirmed}
										startIcon={
											submitting ? (
												<CircularProgress size={16} color='inherit' />
											) : undefined
										}
										sx={dcrReviewStyles.submitBtn}
									>
										{t('Submit')}
									</Button>
								</Box>
							}
						/>
					}
				>
					<Divider sx={dcrReviewStyles.headerDivider} />

					<Stack spacing={2} divider={<Divider flexItem />}>
						{displayChanges.map((entry, i) =>
							isGroupEntry(entry) ? (
								<Stack key={`group-${entry.entityLabel}-${i}`} spacing={1}>
									<ReviewGroupHeader
										label={entry.entityLabel}
										summary={entry.summary}
									/>

									<Stack spacing={1} sx={dcrReviewStyles.nestedFieldsWrapper}>
										{entry.fields?.map((f, fi) => (
											<Stack key={`${f.label}-${fi}`} spacing={0.5}>
												<Typography sx={dcrReviewStyles.changeLabel}>
													{f.label}
												</Typography>
												<Box sx={sx.changeRow}>
													<FieldChange
														previousDisplay={f.previousDisplay}
														newDisplay={f.newDisplay}
														isNewField={f.isNewField}
													/>
												</Box>
											</Stack>
										))}

										{entry.rows && entry.rows.length > 0 && (
											<Stack spacing={0.5}>
												{entry.rows.map((row, ri) => (
													<Box key={`row-${ri}`} sx={sx.changeRow}>
														<FieldChange
															previousDisplay={row.previousDisplay}
															newDisplay={row.newDisplay}
															isNewField={row.previousDisplay === null}
														/>
														{row.isPrimary && (
															<BasicChip
																label={t('Primary')}
																variant='primary'
															/>
														)}
													</Box>
												))}
											</Stack>
										)}

										{entry.wholeItems && entry.wholeItems.length > 0 && (
											<Box sx={sx.wholeItemsGrid}>
												{entry.wholeItems.map((item, wi) => (
													<WholeEntityChange
														key={`whole-${wi}`}
														action={item.action}
														value={item.value}
														chip={item.chip}
														asGridRow
													/>
												))}
											</Box>
										)}
									</Stack>
								</Stack>
							) : (
								<Stack key={`${entry.label}-${i}`} spacing={0.5}>
									<Typography sx={dcrReviewStyles.changeLabel}>
										{entry.label}
									</Typography>
									<Box sx={sx.changeRow}>
										<FieldChange
											previousDisplay={entry.previousDisplay}
											newDisplay={entry.newDisplay}
											isNewField={entry.isNewField}
										/>
									</Box>
								</Stack>
							)
						)}
					</Stack>
				</Modal>
			</DcrContext.Provider>
		</FormProvider>
	);
};
